import React, { useMemo, useState } from 'react';
import { Row, Col, Card, Badge, Button, OverlayTrigger, Tooltip, Form, InputGroup } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import * as moment from 'moment';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { DEV_SERVER } from '../../../constants/urls';
import { userSignal } from '../../../signal/userSignal';
import CountUp from 'react-countup';

/**
 * Renders a farmer's sales (voucher/invoice) history pulled from the Marg ERP.
 * All data merging (Party -> MDis -> Details -> Product) happens on the backend.
 *
 * Props:
 *   farmerId:    string (required)
 *   farmerName:  string (optional, for empty-state copy)
 */
const FarmerSales = ({ farmerId, farmerName }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('sales');
  const [expandedRows, setExpandedRows] = useState({});

  const salesQuery = useQuery(
    ['FarmerSales', farmerId, fromDate, toDate, typeFilter],
    async ({ signal }) => {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (typeFilter === 'all') {
        params.append('includeAllTypes', 'true');
      } else if (typeFilter === 'sales') {
        params.append('types', 'S,B');
      } else if (typeFilter === 'purchases') {
        params.append('types', 'P,R');
      }
      const url = `${DEV_SERVER}/farmer/v2/${farmerId}/sales?${params.toString()}`;
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${userSignal.value?.token}` },
        signal,
      });
      return resp.data;
    },
    {
      enabled: !!farmerId,
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Unable to load sales');
      },
      refetchOnWindowFocus: false,
    },
  );

  const data = salesQuery.data?.data || {};
  const invoices = data.invoices || [];
  const summary = data.summary || {};
  const matchedParties = data.matchedParties || [];
  const topProducts = data.topProducts || [];

  const totalAmount = useMemo(() => summary.totalAmount || 0, [summary]);
  const totalInvoices = summary.totalInvoices || 0;
  const totalQuantity = summary.totalQuantity || 0;
  const lastPurchaseDate = summary.lastPurchaseDate
    ? moment(summary.lastPurchaseDate).format('DD MMM YYYY')
    : '—';

  const handleRefresh = () => {
    salesQuery.refetch();
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  // Renders a numeric cell with thousand separators and a tooltip showing the
  // full untruncated value — fixes overflow in narrow columns (e.g. Qty=20000).
  const numberCell = (value, { prefix = '', decimals = 2 } = {}) => {
    const num = Number(value || 0);
    const display = num.toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    const full = `${prefix}${num.toLocaleString('en-IN', { maximumFractionDigits: 4 })}`;
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip style={{ position: 'fixed' }}>{full}</Tooltip>}>
        <span
          style={{
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {prefix}
          {display}
        </span>
      </OverlayTrigger>
    );
  };

  const invoiceColumns = [
    {
      name: 'Invoice #',
      width: '140px',
      center: true,
      selector: (row) => (
        <OverlayTrigger placement="top" overlay={<Tooltip style={{ position: 'fixed' }}>{row?.vcn}</Tooltip>}>
          <span className="fw-semibold">{row?.vcn || '—'}</span>
        </OverlayTrigger>
      ),
    },
    {
      name: 'Date',
      center: true,
      width: '110px',
      selector: (row) => (row?.date ? moment(row.date).format('DD MMM YY') : '—'),
      sortable: true,
    },
    {
      name: 'Type',
      center: true,
      width: '110px',
      selector: (row) => {
        const isReturn = row?.type === 'B' || row?.type === 'R';
        return <Badge bg={isReturn ? 'warning' : 'success'}>{row?.typeLabel || row?.type || '—'}</Badge>;
      },
    },
    {
      name: 'Items',
      center: true,
      width: '70px',
      selector: (row) => row?.itemCount ?? 0,
    },
    {
      name: 'Qty',
      center: true,
      width: '110px',
      selector: (row) => numberCell(row?.totalQty),
    },
    {
      name: 'Amount',
      center: true,
      width: '160px',
      selector: (row) => {
        const isReturn = row?.type === 'B' || row?.type === 'R';
        const amt = Math.abs(Number(row?.final || 0));
        const display = amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const tooltipText = `${isReturn ? '-' : ''}₹ ${amt.toLocaleString('en-IN', { maximumFractionDigits: 4 })}`;
        return (
          <OverlayTrigger placement="top" overlay={<Tooltip style={{ position: 'fixed' }}>{tooltipText}</Tooltip>}>
            <span
              className={isReturn ? 'text-danger' : 'text-success'}
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {`${isReturn ? '-' : ''}₹ ${display}`}
            </span>
          </OverlayTrigger>
        );
      },
    },
    {
      name: 'MR / Area',
      center: true,
      selector: (row) => [row?.mr, row?.area].filter(Boolean).join(' / ') || '—',
    },
    {
      name: 'Products',
      center: true,
      cell: (row) => (
        <Button variant="link" size="sm" onClick={() => toggleRow(row.id || row.vcn)}>
          {expandedRows[row.id || row.vcn] ? 'Hide' : 'View Lines'}
        </Button>
      ),
    },
  ];

  const expandedComponent = ({ data: row }) => (
    <div style={{ padding: '12px 24px', backgroundColor: '#f8f9fa' }}>
      <h6 className="mb-2">
        {row.typeLabel || row.type} — {row.vcn} — {row.date ? moment(row.date).format('DD MMM YYYY') : '—'}
      </h6>
      <DataTable
        noHeader
        dense
        columns={[
          { name: 'Product', selector: (r) => r.productName || r.gcode || '—', wrap: true, minWidth: '260px' },
          { name: 'Batch', selector: (r) => r.batch || '—', width: '120px', center: true },
          { name: 'Qty', selector: (r) => numberCell(r.qty), center: true, width: '100px' },
          { name: 'Free', selector: (r) => numberCell(r.free), center: true, width: '100px' },
          { name: 'MRP', selector: (r) => numberCell(r.mrp, { prefix: '₹ ' }), center: true, width: '120px' },
          { name: 'Rate', selector: (r) => numberCell(r.rate, { prefix: '₹ ' }), center: true, width: '120px' },
          { name: 'Disc', selector: (r) => numberCell(r.discount), center: true, width: '100px' },
          { name: 'GST %', selector: (r) => numberCell(r.gst), center: true, width: '90px' },
          { name: 'Amount', selector: (r) => numberCell(r.amount, { prefix: '₹ ' }), center: true, width: '130px' },
        ]}
        data={row.items || []}
      />
    </div>
  );

  const SummaryCard = ({ label, value, color, prefix = '', suffix = '', decimals = 0 }) => (
    <Card className="h-100 border-0 shadow-sm">
      <Card.Body className="d-flex flex-column">
        <p className="mb-1 text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </p>
        <h3 className="mb-0" style={{ color }}>
          {prefix}
          <CountUp end={Number(value) || 0} decimals={decimals} separator="," duration={1.2} />
          {suffix}
        </h3>
      </Card.Body>
    </Card>
  );

  if (salesQuery.isLoading || salesQuery.isFetching) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary m-2" role="status">
          <span className="visually-hidden">Loading sales…</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary cards */}
      <Row className="g-3 mb-3">
        <Col sm={6} md={3}>
          <SummaryCard label="Total Invoices" value={totalInvoices} color="#096dd9" />
        </Col>
        <Col sm={6} md={3}>
          <SummaryCard label="Net Amount" value={totalAmount} color={totalAmount < 0 ? '#e05d5d' : '#3daf6a'} prefix="₹ " decimals={2} />
        </Col>
        <Col sm={6} md={3}>
          <SummaryCard label="Net Quantity" value={totalQuantity} color="#d4a534" decimals={2} />
        </Col>
        <Col sm={6} md={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex flex-column">
              <p className="mb-1 text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Last Purchase
              </p>
              <h3 className="mb-0" style={{ color: '#6c5ce7' }}>
                {lastPurchaseDate}
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Matched party banner */}
      {matchedParties.length > 0 && (
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <p className="mb-1 text-muted small text-uppercase">Linked Marg Parties</p>
                <h5 className="mb-0">
                  {matchedParties.map((p, idx) => (
                    <span key={idx} className="me-2">
                      <Badge bg="info" className="me-1">{p.cid}</Badge>
                      {p.partyName || '—'}
                      {p.margCode ? <span className="text-muted"> · {p.margCode}</span> : null}
                    </span>
                  ))}
                </h5>
                {matchedParties[0]?.area || matchedParties[0]?.route ? (
                  <small className="text-muted">
                    {[matchedParties[0]?.area, matchedParties[0]?.route, matchedParties[0]?.mr].filter(Boolean).join(' · ')}
                  </small>
                ) : null}
              </div>
              <Badge bg="success" pill>
                {totalInvoices} invoices synced
              </Badge>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col sm={6} md={3}>
              <Form.Label className="small text-muted">From</Form.Label>
              <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Col>
            <Col sm={6} md={3}>
              <Form.Label className="small text-muted">To</Form.Label>
              <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Col>
            <Col sm={6} md={3}>
              <Form.Label className="small text-muted">Type</Form.Label>
              <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="sales">Sales & Returns</option>
                <option value="purchases">Purchases & Returns</option>
                <option value="all">All Vouchers</option>
              </Form.Select>
            </Col>
            <Col sm={6} md={3} className="d-flex align-items-end">
              <Button variant="primary" className="w-100" onClick={handleRefresh}>
                <i className="fas fa-sync-alt me-2"></i>Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Invoices table */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Header className="d-flex justify-content-between align-items-center bg-white">
          <h5 className="mb-0">Invoices</h5>
          <small className="text-muted">Source: Marg Corporate ERP (synced daily)</small>
        </Card.Header>
        <Card.Body className="p-0">
          <DataTable
            columns={invoiceColumns}
            data={invoices}
            pagination
            paginationPerPage={15}
            paginationRowsPerPageOptions={[10, 15, 25, 50, 100]}
            expandableRows
            expandableRowsComponent={expandedComponent}
            expandableRowExpanded={(row) => !!expandedRows[row.id || row.vcn]}
            onRowExpandToggled={(expanded, row) =>
              setExpandedRows((prev) => ({ ...prev, [row.id || row.vcn]: expanded }))
            }
            highlightOnHover
            noDataComponent={
              <div className="p-4 text-muted text-center">
                {matchedParties.length === 0
                  ? `No Marg ERP data linked to ${farmerName || 'this farmer'} yet. Mapping is done via phone number — please ensure the phone number matches the Marg party record.`
                  : 'No invoices in the selected date/type range.'}
              </div>
            }
            customStyles={{
              headCells: { style: { fontWeight: 600, backgroundColor: '#f8f9fa' } },
              rows: { style: { minHeight: '56px' } },
            }}
          />
        </Card.Body>
      </Card>

      {/* Top products */}
      {topProducts.length > 0 && (
        <Card className="mb-3 border-0 shadow-sm">
          <Card.Header className="bg-white">
            <h5 className="mb-0">Top Products</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <DataTable
              columns={[
                { name: 'Product', selector: (r) => r.productName || r.gcode, wrap: true, minWidth: '300px' },
                { name: 'Unit', selector: (r) => r.unit || '—', center: true, width: '100px' },
                { name: 'Total Qty', selector: (r) => numberCell(r.totalQty), center: true, width: '130px' },
                { name: 'Orders', selector: (r) => r.orderCount || 0, center: true, width: '100px' },
                { name: 'Total Amount', selector: (r) => numberCell(r.totalAmount, { prefix: '₹ ' }), center: true, width: '170px' },
              ]}
              data={topProducts}
              dense
              customStyles={{ headCells: { style: { fontWeight: 600, backgroundColor: '#f8f9fa' } } }}
            />
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default FarmerSales;
